import { useId } from 'react';
import { createPortal } from 'react-dom';
import CloseButton from './CloseButton';
import useLockBodyScroll from '../hooks/useLockBodyScroll';
import { useModalFocus } from '../hooks/useModalFocus';

interface Props {
  isOpen: boolean;
  title?: string;
  eyebrow: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  panelClassName?: string;
}

export default function Modal({ isOpen, title, eyebrow, onClose, children, footer, panelClassName = '' }: Props) {
  useLockBodyScroll(isOpen);
  const { panelRef, onKeyDown } = useModalFocus(isOpen, onClose);
  const titleId = useId();
  const eyebrowId = useId();

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const labelledBy = title ? titleId : eyebrowId;

  return createPortal(
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/40"
      onClick={onClose}
    >
      <div className="flex min-h-full items-center justify-center px-3 py-6 sm:px-4 sm:py-10">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          className={`w-full max-w-3xl rounded-3xl border border-white/70 bg-white p-5 shadow-2xl shadow-slate-900/20 focus:outline-none sm:p-8 ${panelClassName}`.trim()}
          onClick={(event) => event.stopPropagation()}
        >
        <div className="flex items-start justify-between gap-4">
          <div>
            {eyebrow && (
              <p id={eyebrowId} className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">{eyebrow}</p>
            )}
            {title && (
              <h2 id={titleId} className="mt-2 text-2xl font-semibold text-slate-600">{title}</h2>
            )}
          </div>
          <CloseButton onClick={onClose} />
        </div>
        <div className="mt-6">{children}</div>
        {footer ? (
          <div className="mt-5 flex items-center justify-end gap-3 border-t border-slate-200/70 pt-4">
            {footer}
          </div>
        ) : null}
        </div>
      </div>
    </div>
    ,
    document.body
  );
}
